import { useCallback } from 'react';
import { useLocalStorage } from 'react-use';

import useTracking from '@zen/utils/hooks/useTracking';

import type { ColumnWidthMap } from '../helpers';
import { TableConfigurationTrackingAction, TableConfigurationTrackingCategory } from '../types';

export const useColumnWidths = (
  tableId: string,
  persistInLocalStorage?: boolean
): [ColumnWidthMap, (columnWidths: ColumnWidthMap) => void] => {
  const storageKey = 'table-column-widths';

  const [storedWidths, setStoredWidths] = useLocalStorage<Record<string, ColumnWidthMap>>(storageKey, {});

  const { trackEvent } = useTracking();

  const setColumnWidths = useCallback(
    (newWidths: ColumnWidthMap) => {
      trackEvent({
        category: TableConfigurationTrackingCategory,
        action: TableConfigurationTrackingAction.RESIZE,
        label: tableId
      });

      if (persistInLocalStorage) {
        setStoredWidths((previous) => ({ ...previous, [tableId]: newWidths }));
      }
    },
    [tableId, trackEvent, persistInLocalStorage, setStoredWidths]
  );

  const widths: ColumnWidthMap = persistInLocalStorage ? storedWidths?.[tableId] || {} : {};

  return [widths, setColumnWidths];
};
